import React from 'react';
import i18n from '@/i18n/config';
import { Autocomplete, AutocompleteItem, Chip } from '@heroui/react';
import type { Key } from '@react-types/shared';

import { useShopOptions } from '@/hooks/leads/useShopOptions';

interface FilterShopMultiSelectProps {
  label: string;
  value: string; // comma-separated shop keys
  onChange: (value: string) => void;
  className?: string;
}

export function FilterShopMultiSelect({ label, value, onChange, className = 'w-64' }: FilterShopMultiSelectProps) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedShops, setSelectedShops] = React.useState<Array<{ key: string; label: string }>>([]);
  const shopOptions = useShopOptions();

  // Parse comma-separated keys into array
  const selectedShopKeys = React.useMemo(() => {
    if (!value || value.trim() === '') {
      return [];
    }
    return value
      .split(',')
      .map((k) => k.trim())
      .filter((k) => k !== '');
  }, [value]);

  // Update selected shops when value changes
  React.useEffect(() => {
    const selected = shopOptions.filter((shop) => selectedShopKeys.includes(shop.key));
    setSelectedShops(selected);
  }, [selectedShopKeys.join(','), shopOptions]);

  // Filter options based on search term
  const filteredOptions = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return shopOptions.filter((shop) => !selectedShopKeys.includes(shop.key));
    }
    const term = searchTerm.toLowerCase();
    return shopOptions.filter(
      (shop) =>
        !selectedShopKeys.includes(shop.key) &&
        (shop.label.toLowerCase().includes(term) || shop.key.toLowerCase().includes(term))
    );
  }, [searchTerm, selectedShopKeys, shopOptions]);

  const handleShopAdd = React.useCallback(
    (key: Key | null) => {
      if (!key) return;

      const shopKey = String(key);
      if (selectedShopKeys.includes(shopKey)) return;

      const shop = shopOptions.find((s) => s.key === shopKey);
      if (!shop) return;

      const newSelectedShops = [...selectedShops, shop];
      setSelectedShops(newSelectedShops);

      // Update value with comma-separated keys
      const newValue = newSelectedShops.map((s) => s.key).join(',');
      onChange(newValue);

      setSearchTerm('');
    },
    [selectedShops, shopOptions, selectedShopKeys, onChange]
  );

  const handleShopRemove = React.useCallback(
    (shopKey: string) => {
      const newSelectedShops = selectedShops.filter((s) => s.key !== shopKey);
      setSelectedShops(newSelectedShops);

      const newValue = newSelectedShops.map((s) => s.key).join(',');
      onChange(newValue);
    },
    [selectedShops, onChange]
  );

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <label className="text-default-700 text-sm font-medium">{label}</label>
      <Autocomplete
        inputValue={searchTerm}
        onInputChange={setSearchTerm}
        selectedKey={null}
        onSelectionChange={handleShopAdd}
        isLoading={false}
        placeholder={i18n.t('copy.search_shop')}
        size="sm"
        allowsCustomValue={false}
        onOpenChange={() => {}}
      >
        {filteredOptions.map((shop) => (
          <AutocompleteItem key={shop.key} textValue={shop.label}>
            <div className="flex flex-col">
              <span className="font-medium">{shop.label}</span>
            </div>
          </AutocompleteItem>
        ))}
      </Autocomplete>
      {selectedShops.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {selectedShops.map((shop) => (
            <Chip key={shop.key} size="sm" variant="flat" onClose={() => handleShopRemove(shop.key)}>
              {shop.label}
            </Chip>
          ))}
        </div>
      )}
    </div>
  );
}

export default FilterShopMultiSelect;
